"use client";

import type { FC } from "react";
import type { PractitionerPublicProfileDto } from "cmw-client";
import { PractitionerCard } from "@/components/practitioner-card";
import { cn } from "@/lib/cn";
import { DirectoryEmptyState } from "./directory-empty-state";

type PractitionerListProps = {
  practitioners: PractitionerPublicProfileDto[];
  isPending: boolean;
  isError: boolean;
  error?: unknown;
  isLoadingMore?: boolean;
  isFiltered?: boolean;
  isLocated?: boolean;
  onSearch: () => void;
  onNearMe: () => void;
  onClearFilters?: () => void;
  isLocating?: boolean;
  className?: string;
};

const SKELETONS = 3;

const emptyDescription = ({ isFiltered, isLocated }: { isFiltered?: boolean; isLocated?: boolean }) => {
  if (isFiltered && isLocated)
    return "We couldn't find any practitioners matching your current filters. Try clearing a filter or widening your search location.";
  if (isFiltered) return "We couldn't find any practitioners matching your current filters. Try clearing one.";
  if (isLocated)
    return "We couldn't find any practitioners near that location. Try searching a nearby suburb or postcode.";
  return "We couldn't find any practitioners matching your current filters. Try adjusting your search location.";
};

export const PractitionerList: FC<PractitionerListProps> = ({
  practitioners,
  isPending,
  isError,
  error,
  isLoadingMore,
  isFiltered,
  isLocated,
  onSearch,
  onNearMe,
  onClearFilters,
  isLocating,
  className,
}) => {
  if (isPending) {
    return (
      <div className={cn("flex flex-col gap-24", className)}>
        {Array.from({ length: SKELETONS }, (_, index) => (
          <PractitionerCard key={index} loading />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className={cn("text-16 text-brand-red", className)}>
        Could not load practitioners: {error instanceof Error ? error.message : "unknown error"}
      </p>
    );
  }

  if (practitioners.length === 0) {
    return (
      <DirectoryEmptyState
        description={emptyDescription({ isFiltered, isLocated })}
        onSearch={onSearch}
        onNearMe={onNearMe}
        onClearFilters={isFiltered ? onClearFilters : undefined}
        isLocating={isLocating}
        className={className}
      />
    );
  }

  return (
    <div className={cn("flex flex-col gap-24", className)}>
      {practitioners.map((practitioner) => (
        <PractitionerCard key={practitioner.username} source={practitioner} />
      ))}
      {isLoadingMore && <PractitionerCard loading />}
    </div>
  );
};
